import { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  makeStyles, tokens, Text, Button, Spinner, Switch,
  MessageBar, MessageBarBody, useToastController, Toast, ToastTitle,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular, DeleteRegular, ChevronDownRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { Chip } from '../components/Chip.js';
import { GLOBAL_TOASTER_ID } from '../components/ThemedProvider.js';
import { useIsEcosoftStaff } from '../stores/authStore.js';
import {
  getImportaciones, borrarImportacion,
  type ImportacionReportada, type Atasco, type RespuestaImportaciones,
} from '../api/staffImportaciones.js';

/**
 * CÓMO SALIERON LAS IMPORTACIONES A OPUS 27 — solo staff Ecosoft.
 *
 * Existe para no volver a depender de que el cliente nos avise. Cuando una importación falla,
 * el error queda en un archivo de log en SU máquina; si no lo manda, no existe para nosotros.
 * El 2026-08-29 un cliente perdió los 222 renglones de una hoja por una clave de insumo
 * repetida y lo supimos dos días después, por WhatsApp. Un mes antes había pasado lo mismo por
 * otra puerta.
 *
 * Muestra por defecto **solo las que fallaron**, que son las que hay que mirar. El historial
 * completo está a un interruptor de distancia.
 *
 * Cada fila se puede BORRAR una vez resuelta. Se borra el REPORTE, nunca nada del cliente: su
 * presupuesto y su obra quedan intactos.
 */

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-canvas)' },
  body: { padding: '20px 28px 64px', maxWidth: '1100px', margin: '0 auto' },
  head: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '18px' },
  small: { fontSize: tokens.fontSizeBase200, color: 'var(--text-secondary)' },
  barra: {
    display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap',
    padding: '12px 14px', marginBottom: '14px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  derecha: { marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' },
  fila: {
    display: 'flex', flexDirection: 'column', gap: '6px',
    padding: '14px 16px', marginBottom: '10px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  filaFallo: { borderLeft: '4px solid var(--error, #d13438)' },
  filaParcial: { borderLeft: '4px solid var(--warn, #d29922)' },
  filaOk: { borderLeft: '4px solid var(--ok, #2ea043)' },
  cabeceraFila: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' },
  quien: { display: 'flex', gap: '14px', flexWrap: 'wrap', fontSize: tokens.fontSizeBase200, color: 'var(--text-secondary)' },
  detalle: {
    marginTop: '6px', padding: '10px 12px',
    background: 'var(--bg-canvas)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex', flexDirection: 'column', gap: '6px',
  },
  error: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, wordBreak: 'break-word' },
  vacio: { padding: '40px 0', textAlign: 'center', color: 'var(--text-secondary)' },
  versionAlDia: { color: 'var(--text-secondary)' },
  versionVieja: { color: 'var(--warn, #d29922)', fontWeight: tokens.fontWeightSemibold },
  versionDudosa: { color: 'var(--text-secondary)', fontStyle: 'italic' },

  // "Se lo llevó y no volvió" — la sección que hace visible al cliente atascado.
  seccion: { display: 'flex', flexDirection: 'column', gap: '4px', margin: '26px 0 12px' },
  filaAtasco: { borderLeft: '4px solid var(--error, #d13438)' },
  filaInsistiendo: { borderLeft: '4px solid var(--warn, #d29922)' },
  titulo: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
});

/** El color de la versión: sólo se resalta la que obliga a hacer algo (pedirle que
 *  actualice). Al día y "no se puede saber" no piden acción, así que no gritan. */
function estadoVersion(
  estado: ImportacionReportada['version']['estado'],
  s: ReturnType<typeof useStyles>,
): string {
  if (estado === 'desactualizado') return s.versionVieja;
  if (estado === 'sin_fecha_confiable') return s.versionDudosa;
  return s.versionAlDia;
}

const fecha = (iso: string): string => new Date(iso).toLocaleString('es-MX', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

export function StaffImportacionesPage(): JSX.Element {
  const s = useStyles();
  const isStaff = useIsEcosoftStaff();
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  const [filas, setFilas] = useState<ImportacionReportada[] | null>(null);
  const [atascos, setAtascos] = useState<Atasco[]>([]);
  const [umbrales, setUmbrales] = useState<RespuestaImportaciones['umbrales'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [todas, setTodas] = useState(false);
  const [abierta, setAbierta] = useState<string | null>(null);
  const [borrando, setBorrando] = useState<string | null>(null);

  const cargar = useCallback(async (verTodas: boolean) => {
    try {
      setError(null);
      const r = await getImportaciones(verTodas ? 'todas' : 'fallidas');
      setFilas(r.importaciones);
      setAtascos(r.atascos);
      setUmbrales(r.umbrales ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar');
    }
  }, []);

  useEffect(() => { void cargar(todas); }, [cargar, todas]);

  const borrar = async (id: string): Promise<void> => {
    setBorrando(id);
    try {
      await borrarImportacion(id);
      setFilas((f) => (f ?? []).filter((x) => x.id !== id));
      dispatchToast(
        <Toast><ToastTitle>Reporte quitado de la lista</ToastTitle></Toast>,
        { intent: 'success', timeout: 2500 },
      );
    } catch (e) {
      dispatchToast(
        <Toast><ToastTitle>{e instanceof Error ? e.message : 'No se pudo quitar'}</ToastTitle></Toast>,
        { intent: 'error', timeout: 4000 },
      );
    } finally {
      setBorrando(null);
    }
  };

  if (!isStaff) return <Navigate to="/" replace />;

  const conProblema = (filas ?? []).filter((f) => f.resultado !== 'ok').length;

  return (
    <div className={s.page}>
      <AppTopBar />
      <div className={s.body}>
        <Breadcrumbs items={[{ label: 'Inicio', to: '/' }, { label: 'Importaciones a OPUS 27' }]} />

        <div className={s.head}>
          <Text size={600} weight="semibold">Importaciones a OPUS 27</Text>
          <span className={s.small}>
            Quién se llevó su presupuesto a OPUS 27 y cómo le fue. Antes de esto, cuando una
            importación fallaba el error quedaba en la máquina del cliente y sólo nos
            enterábamos si él lo reportaba — y si nunca terminaba de importar, ni eso.
          </span>
        </div>

        {/* ══════════════════════════════════════════════════════════════════════════
            SE LO LLEVÓ Y NO VOLVIÓ. Va PRIMERO porque es el caso peor y el que hasta hoy
            era invisible: la lista de abajo sólo muestra a quien OPUS 27 alcanzó a
            reportar, y quien se lleva el paquete y nunca vuelve no aparecía en ningún
            lado. El 01/09 un cliente descargó su presupuesto doce veces sin poder
            importarlo ni una, y nos enteramos mirando la actividad a mano.

            No aparece una descarga suelta: nadie está atascado por bajar el archivo una
            vez. Hace falta que INSISTA. Los números y su medición: el servidor los manda
            en `umbrales` y el porqué está en `services/seLoLlevoYNoVolvio.ts`.
            ══════════════════════════════════════════════════════════════════════════ */}
        <div className={s.seccion}>
          <Text size={500} weight="semibold">Se lo llevaron y no volvieron</Text>
          <span className={s.small}>
            Gente que bajó el mismo presupuesto varias veces y de la que OPUS 27 nunca
            reportó una importación. Esto no depende de qué versión tenga el cliente: los
            binarios viejos no reportan nunca, ni cuando la importación sale bien.
            {umbrales && ` Se muestra a partir de ${umbrales.descargasMin} descargas del mismo`
              + ` presupuesto, esperando ${umbrales.esperaMinutos} min después de la última`
              + ` (importar tarda segundos). Últimos ${umbrales.dias} días.`}
          </span>
        </div>

        {atascos.length === 0 && (
          <div className={s.vacio}>
            <Text>Nadie se quedó a medio camino.</Text>
          </div>
        )}

        {atascos.map((a) => (
          <div key={a.id} className={`${s.fila} ${a.estado === 'no_volvio' ? s.filaAtasco : s.filaInsistiendo}`}>
            <div className={s.cabeceraFila}>
              <Chip
                label={a.estado === 'no_volvio' ? 'No volvió' : 'Sigue insistiendo'}
                dotColor={a.estado === 'no_volvio' ? 'var(--error, #d13438)' : 'var(--warn, #d29922)'}
              />
              {/* La frase la arma el servidor: "Se lo llevó a las 10:01 y no volvió". */}
              <span className={s.titulo}>{a.titulo}</span>
              <span className={s.small}>{a.detalle}</span>
            </div>
            <div className={s.quien}>
              <span>{a.empresa ?? '—'}</span>
              <span>{a.usuario ?? '—'}</span>
              <span>{a.presupuesto ?? '(sin nombre)'}</span>
              {a.conceptos != null && <span>{a.conceptos} conceptos</span>}
              <span>
                {a.ventanaMinutos > 0 ? `insistió ${a.ventanaMinutos} min · ` : ''}
                última descarga {fecha(a.ultima)}
              </span>
              {/* CON QUÉ OPUS 27 ESTÁ PELEANDO. Sólo aparece cuando su binario se
                  identifica; mientras no lo haga, la fila sirve igual sin este dato. */}
              {a.opus27 && (
                <span className={estadoVersion(a.opus27.veredicto.estado, s)}>
                  {a.opus27.veredicto.etiqueta}
                </span>
              )}
            </div>
            {a.opus27 && a.opus27.veredicto.leFalta.length > 0 && (
              <span className={s.small}>Le falta: {a.opus27.veredicto.leFalta.join(' · ')}.</span>
            )}
            {a.estado === 'insistiendo' && (
              <span className={s.small}>
                La última descarga es de hace {a.silencioMinutos} min: todavía podría estar importando.
              </span>
            )}
            <span className={s.small}>Presupuesto: {a.quoteId}</span>
          </div>
        ))}

        <div className={s.seccion}>
          <Text size={500} weight="semibold">Lo que OPUS 27 sí reportó</Text>
          <span className={s.small}>
            Cada intento que llegó a terminar y avisar. Sólo aparece si el cliente tiene
            OPUS 27 del 31/08/2026 en adelante: los binarios anteriores no reportan.
          </span>
        </div>

        <div className={s.barra}>
          <Switch
            checked={todas}
            onChange={(_, d) => setTodas(d.checked)}
            label={todas ? 'Todas las importaciones' : 'Solo las que fallaron'}
          />
          <div className={s.derecha}>
            {filas !== null && (
              <span className={s.small}>
                {todas ? `${filas.length} en total · ${conProblema} con problemas` : `${filas.length} con problemas`}
              </span>
            )}
            <Button icon={<ArrowClockwiseRegular />} appearance="subtle" onClick={() => void cargar(todas)}>
              Actualizar
            </Button>
          </div>
        </div>

        {error && (
          <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>
        )}

        {filas === null && !error && <Spinner label="Cargando…" />}

        {filas !== null && filas.length === 0 && (
          <div className={s.vacio}>
            <Text>{todas ? 'Todavía no hay importaciones registradas.' : 'Ninguna importación falló. '}</Text>
            {!todas && <div className={s.small}>Prendé el interruptor para ver el historial completo.</div>}
          </div>
        )}

        {(filas ?? []).map((f) => {
          const clase = f.resultado === 'fallo' ? s.filaFallo : f.resultado === 'parcial' ? s.filaParcial : s.filaOk;
          const abierto = abierta === f.id;
          return (
            <div key={f.id} className={`${s.fila} ${clase}`}>
              <div className={s.cabeceraFila}>
                <Chip
                  label={f.resultado === 'ok' ? 'Entró completa' : f.resultado === 'parcial' ? 'Entró a medias' : 'No entró'}
                  dotColor={f.resultado === 'ok' ? 'var(--ok, #2ea043)' : f.resultado === 'parcial' ? 'var(--warn, #d29922)' : 'var(--error, #d13438)'}
                />
                <Text weight="semibold">{f.proyecto ?? f.presupuesto ?? '(sin nombre)'}</Text>
                <span className={s.small}>{fecha(f.fecha)}</span>
                <div className={s.derecha}>
                  <Button
                    appearance="subtle"
                    icon={abierto ? <ChevronDownRegular /> : <ChevronRightRegular />}
                    onClick={() => setAbierta(abierto ? null : f.id)}
                  >
                    Detalle
                  </Button>
                  <Button
                    appearance="subtle"
                    icon={<DeleteRegular />}
                    disabled={borrando === f.id}
                    onClick={() => void borrar(f.id)}
                  >
                    Quitar
                  </Button>
                </div>
              </div>

              <div className={s.quien}>
                <span>{f.empresa ?? '—'}</span>
                <span>{f.usuario ?? '—'}</span>
                {f.obra && <span>obra: {f.obra}</span>}
                <span>
                  {f.conceptosImportados} entraron · {f.conceptosFallidos} quedaron afuera
                </span>
                {/* CON QUÉ VERSIÓN IMPORTÓ, Y SI TIENE QUE ACTUALIZAR. La frase la arma el
                    servidor: acá sólo se pinta. Un reporte mudo NO es un guion — es un
                    binario anterior al 31/08, y eso es exactamente lo que hay que saber
                    antes de perseguir un fantasma. */}
                <span className={estadoVersion(f.version.estado, s)}>{f.version.etiqueta}</span>
              </div>

              {abierto && (
                <div className={s.detalle}>
                  {/* QUÉ ARREGLO LE FALTA, con nombre. Es lo que se le dice al cliente cuando
                      se le pide que actualice: sin esto la petición es "actualizá porque sí",
                      y con esto se le puede explicar qué se lleva. */}
                  {f.version.leFalta.length > 0 && (
                    <span className={s.small}>
                      Le falta: {f.version.leFalta.join(' · ')}.
                    </span>
                  )}
                  {/* El número de ensamblado se muestra acá abajo y en ningún lado más: es el
                      mismo en todas las compilaciones de m1, así que informa sobre el binario
                      lo mismo que no decir nada. Está por si algún día empieza a moverse. */}
                  {f.versionM1 && (
                    <span className={s.small}>
                      Número de ensamblado: {f.versionM1} (igual en todas las compilaciones)
                    </span>
                  )}
                  {(f.contexto.fase || f.contexto.destino) && (
                    <span className={s.small}>
                      {f.contexto.fase === 'A' && 'Falló trayendo los recursos del catálogo (fase A). '}
                      {f.contexto.fase === 'B' && 'Falló armando el análisis (fase B). '}
                      {f.contexto.destino === 'obra_nueva' && 'Destino: obra nueva.'}
                      {f.contexto.destino === 'obra_existente' && 'Destino: una obra que ya existía.'}
                    </span>
                  )}
                  {(f.contexto.catalogos ?? []).map((c, i) => (
                    <span key={`cat-${i}`} className={s.small}>
                      catálogo {c.catalogoM0} → obra {c.obraM1}
                      {c.recursosTransferidos != null ? ` · ${c.recursosTransferidos} recursos` : ''}
                    </span>
                  ))}
                  {f.contexto.conceptoEnCurso?.clave && (
                    <span className={s.small}>
                      Se cortó en el concepto {f.contexto.conceptoEnCurso.clave}
                      {f.contexto.conceptoEnCurso.esSintetico ? ' (con análisis por IA)' : ''}
                    </span>
                  )}
                  {f.errores.length === 0 && <span className={s.small}>No se registró ningún error con detalle.</span>}
                  {f.errores.map((e, i) => (
                    <div key={`${e.clave}-${i}`} className={s.error}>
                      <strong>{e.clave || '(sin clave)'}</strong> — {e.mensaje}
                    </div>
                  ))}
                  <span className={s.small}>Presupuesto: {f.quoteId}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
